import { useContext, useEffect } from "react";
import { PropTypes } from "prop-types";

import ReactGA from "react-ga4";

import ActionButton from "../formInputs/buttons/ActionButton";
import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";
import EprStudentInfoBlock from "../studentInfoBlock/EprStudentInfoBlock";
import T from "../../components/translations/Translations";

import { GlobalContext } from "../contextProvider/ContextProvider";

import "../../styles/EprLinks.scss";

const Archives = ({ downloadPdf, loader, resultMessages, selectedStudent, setLoader }) => {
    const { dispatch, state } = useContext(GlobalContext);
    const { eprGradingProfiles, schoolYearDto, token } = state;

    const archivedResources = T({ key: "Archived_Resources" });
    const eprNoData = T({ key: "EPR_No_Data" });

    useEffect(() => {
        const page =
            process.env.NODE_ENV !== "production" ? "/guardian/archived-eprs -> dev" : "/guardian/archived-eprs";
        ReactGA.send({
            hitType: "pageview",
            page,
            title: document.title
        });
    }, []);

    useEffect(() => {
        if (!schoolYearDto || !selectedStudent?.studentNumber || !token) {
            return;
        }
        if (eprGradingProfiles && eprGradingProfiles.length > 0) {
            if (parseInt(eprGradingProfiles[0].studentNumber, 10) === parseInt(selectedStudent?.studentNumber, 10)) {
                return;
            }
        }

        /**
         * @name fetchEprArchive
         * @description Check if the student has an EPR archive
         * @param {{}} app
         * @returns {{}} updatedApp
         */
        const fetchEprArchive = async () => {
            try {
                const eprOptions = {
                    action: "archiveGradingProfilesRead",
                    schoolYearKey: schoolYearDto.key,
                    studentNumber: selectedStudent.studentNumber,
                    token
                };
                setLoader(true);
                const response = await ElementaryProgressReportDao(eprOptions);
                if (response && response?.data) {
                    const { payload } = response.data;
                    payload?.sort((a, b) => {
                        return parseInt(a.key, 10) < parseInt(b.key, 10) ? 1 : -1;
                    });
                    if (payload && payload.length > 0) {
                        dispatch({
                            type: "EprGradingProfiles",
                            eprGradingProfiles: Object.values(payload)
                        });
                    }
                }
            } catch (error) {
                console.error("Unable to retrieve EPR Archive:\n", error);
            } finally {
                setLoader(false);
            }
        };

        fetchEprArchive();
    }, [dispatch, eprGradingProfiles, schoolYearDto, selectedStudent, setLoader, token]);

    return (
        <div>
            <h6 className="ms-4 mt-2">{T({ key: "Archived_Description" })}</h6>
            <div className="grey-background">
                {selectedStudent && <EprStudentInfoBlock setLoader={setLoader} studentInfo={selectedStudent} />}
                {selectedStudent && <h5 className="ms-3">{archivedResources}</h5>}
                <div className="display-container">
                    <div className="epr-links-container">
                        {eprGradingProfiles &&
                            eprGradingProfiles.map((obj) => {
                                const { gradeName, key, schoolYearName, teacherName } = obj;
                                let grade = gradeName;
                                if (
                                    ["K", "Kindergarten", "K_FULL", "K_HALF", "K_HALF_AM", "K_HALF_PM"].includes(
                                        gradeName
                                    )
                                ) {
                                    grade = "Kindergarten";
                                } else {
                                    grade = `${gradeName} Grade`;
                                }
                                return (
                                    <ActionButton
                                        className="action-button-reg epr-link"
                                        key={obj.key}
                                        label={`${schoolYearName} -- ${grade} -- ${teacherName}`}
                                        onClick={() => {
                                            downloadPdf(key, schoolYearName);
                                        }}
                                    />
                                );
                            })}
                        {selectedStudent && !loader && !eprGradingProfiles && <div className="m-4">{eprNoData}</div>}
                    </div>
                    <div className="results-container">
                        {resultMessages &&
                            resultMessages.map((message, index) => {
                                return (
                                    <p key={index} className={message.error ? "error" : "success"}>
                                        {message.message}
                                    </p>
                                );
                            })}
                    </div>
                </div>
            </div>
        </div>
    );
};

Archives.propTypes = {
    downloadPdf: PropTypes.func.isRequired,
    loader: PropTypes.bool.isRequired,
    resultMessages: PropTypes.array.isRequired,
    selectedStudent: PropTypes.object.isRequired,
    setLoader: PropTypes.func.isRequired
};
export default Archives;
